// Moving a Pocket Export into Blackmount: The Import, Step by Step
//
// Short how-to. Primary target: "import Pocket export".
// Long-tail: "pocket.zip to bookmarks", "convert Pocket CSV", "Pocket migration".
// Ported from marketing-blackmount/content/articles/pocket-migration-guide.md
// (2026-02-21). Facts: Pocket shut down 2025-07-08, export deadline
// 2025-11-12. Blackmount /import accepts Netscape bookmarks HTML.

import React from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const AUTHORS = ['Mehrdad Shirangi'];
const PUBLISHED_AT = '2026-08-12';
const UPDATED_AT = '2026-08-12';

const meta = {
  slug: 'import-pocket-export-blackmount',
  title: 'Moving a Pocket Export into Blackmount: The Import, Step by Step',
  description: 'Turn a pocket.zip or Pocket CSV export into a bookmarks HTML file and import it into Blackmount — the converter route, the manual route, and what does not survive the move.',
  publishedAt: PUBLISHED_AT,
  updatedAt: UPDATED_AT,
  authors: AUTHORS,
  tags: ['import pocket export', 'pocket migration', 'pocket.zip', 'bookmarks html', 'pocket alternative'],
  readingTime: '3 min',
  ogImage: '/blackmount-logo.jpg',
};

function Section({ id, title, children }) {
  return (
    <Box component="section" id={id} sx={{ mt: 5 }}>
      <Typography variant="h4" component="h2" fontWeight={700} sx={{ mb: 2, scrollMarginTop: 96 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.75, fontSize: '1.05rem' }}>
      {children}
    </Typography>
  );
}

function Body() {
  return (
    <>
      <P>
        Mozilla shut Pocket down on July 8, 2025, and stopped serving exports on November 12,
        2025. If you saved your archive before that deadline, this is how to get those links
        into <MuiLink component={RouterLink} to="/">Blackmount</MuiLink>.
      </P>

      <Section id="what-you-have" title="What your export looks like">
        <P>
          Most people received a <strong>pocket.zip</strong> by email. Inside are CSV files
          holding the URL, title, save date, and tags for every item you saved. A smaller group
          exported earlier from the legacy getpocket.com/export page and has an{' '}
          <strong>ril_export.html</strong> file instead — that is already a Netscape bookmarks
          file, so skip straight to the upload step.
        </P>
        <P>
          Blackmount's <MuiLink component={RouterLink} to="/imports">import page</MuiLink> (sign in
          first) accepts bookmarks HTML, the same format Chrome, Firefox, and Safari produce when
          you export bookmarks. A ZIP of CSVs needs one conversion step first.
        </P>
      </Section>

      <Section id="converter" title="Route 1: convert the CSV (best for large libraries)">
        <Box component="ol" sx={{ pl: 3, mb: 2, '& li': { mb: 1, lineHeight: 1.7 } }}>
          <li>Unzip <strong>pocket.zip</strong>.</li>
          <li>
            Run an open-source converter over the CSV files — for example{' '}
            <MuiLink href="https://github.com/enjikaka/pocket-to-bookmark" target="_blank" rel="noopener noreferrer">pocket-to-bookmark</MuiLink>,
            which turns a Pocket CSV into Netscape bookmarks HTML.
          </li>
          <li>
            Confirm the output is a single <strong>bookmarks.html</strong> file, and sanity-check
            it before uploading: the number of links in the HTML should roughly match the number
            of rows in the CSV. Pocket exports often split archived and unread items into
            separate CSVs, and converting only one of them is the most common way people lose
            half their library in transit.
          </li>
          <li>Open the <MuiLink component={RouterLink} to="/imports">import page</MuiLink> in Blackmount.</li>
          <li>Upload the file. Your saved links appear in Blackmount, tags included.</li>
        </Box>
        <P>
          A large library imports as one flat set. If you would rather it arrive already
          organized, split the CSV by tag before converting and run one file per topic — you end
          up with a session per subject instead of a single wall of links.
        </P>
      </Section>

      <Section id="manual" title="Route 2: batch it by hand (no tools)">
        <P>
          Fine for libraries under roughly 200 links, and it has the side benefit of letting you
          group items into meaningful sessions as you go.
        </P>
        <Box component="ol" sx={{ pl: 3, mb: 2, '& li': { mb: 1, lineHeight: 1.7 } }}>
          <li>Unzip <strong>pocket.zip</strong> and open the CSV in Excel, Google Sheets, or Numbers.</li>
          <li>Copy a batch of 20 to 30 URLs from the URL column.</li>
          <li>Open them as tabs — paste and go one at a time, or use a multi-URL opener extension.</li>
          <li>Click the Blackmount extension and save all open tabs as one named session.</li>
          <li>Repeat with the next batch until the list is empty.</li>
        </Box>
      </Section>

      <Section id="what-transfers" title="What survives the move">
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small" sx={{ minWidth: 480 }} aria-label="What transfers from a Pocket export into Blackmount">
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell><strong>Pocket data</strong></TableCell>
                <TableCell><strong>Outcome</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>URLs</TableCell><TableCell>Imported as saved tabs</TableCell></TableRow>
              <TableRow><TableCell>Titles</TableCell><TableCell>Preserved</TableCell></TableRow>
              <TableRow><TableCell>Tags</TableCell><TableCell>Mapped to Blackmount tags</TableCell></TableRow>
              <TableRow><TableCell>Date saved</TableCell><TableCell>Mapped to session creation date</TableCell></TableRow>
              <TableRow><TableCell>Highlights</TableCell><TableCell>Not in the export — proprietary, no standard format</TableCell></TableRow>
              <TableRow><TableCell>Cached article text</TableCell><TableCell>Not in the export — Pocket stored it server-side</TableCell></TableRow>
              <TableRow><TableCell>Read / unread status</TableCell><TableCell>No equivalent; Blackmount organizes by session, not a read queue</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section id="missed-deadline" title="If you missed the deadline">
        <P>
          There is no recovery path. Mozilla retained nothing after November 12, 2025. What you
          can do is stop depending on a single service's export button: Blackmount saves
          sessions with their tabs, notes, and voice recordings, works offline in Guest Mode with
          no account, and lets you export at any time. Weighing replacements more broadly? See
          the{' '}
          <MuiLink component={RouterLink} to="/blog/pocket-alternatives-2026">
            2026 Pocket alternatives comparison
          </MuiLink>.
        </P>
      </Section>

      <Divider sx={{ my: 5 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Stuck on a conversion, or holding an export format not covered here? Email{' '}
        <MuiLink href="mailto:support@blackmount.ai">support@blackmount.ai</MuiLink> — we update
        this guide as new Pocket export formats turn up.
      </Typography>
    </>
  );
}

export default { ...meta, render: Body };
